import { type CalendarDate, today } from '@internationalized/date';
import type { CalendarState } from '@react-stately/calendar';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { useCalendarCell } from 'react-aria';

import { useDatePickerConfig } from '../../contexts/ConfigContext';
import { useDatePickerActions } from '../../Dropdown/DropdownContext';
import { useDatePickerValidation } from '../../Input/ValidationContext';

interface CalendarCellProps {
  state: CalendarState;
  date: CalendarDate;
}

export function CalendarCell({ state, date }: CalendarCellProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const { color, isControlled } = useDatePickerConfig();
  const { onClose, onDateChange } = useDatePickerActions();
  const validation = useDatePickerValidation();
  const [isHovered, setIsHovered] = useState(false);

  const { cellProps, buttonProps, isSelected, isFocused, formattedDate, isOutsideVisibleRange } = useCalendarCell(
    { date: date },
    state,
    ref,
  );

  // Get the actual disabled state from cellProps (this includes isDateUnavailable)
  const isDisabled = cellProps['aria-disabled'] === true || cellProps['aria-disabled'] === 'true';

  // Check if this is today
  const todayDate = today(state.timeZone);
  const isToday = date.compare(todayDate) === 0;

  // Don't render dates outside the current month
  if (isOutsideVisibleRange) {
    return <div {...cellProps} style={{ width: `${CELL_SIZE}px`, height: `${CELL_SIZE}px` }} />;
  }

  // Compute dynamic styles based on state
  const getButtonStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      width: `${CELL_SIZE}px`,
      height: `${CELL_SIZE}px`,
    };

    // Priority 1: Disabled state
    if (isDisabled) {
      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        opacity: 0.4,
      };
    }

    // Priority 2: Selected state
    if (isSelected) {
      return {
        ...baseStyle,
        backgroundColor: 'var(--bs-primary)',
      };
    }

    // Priority 3: Keyboard focus (accessibility)
    if (isFocused) {
      return {
        ...baseStyle,
        backgroundColor: 'rgba(var(--bs-secondary-rgb), 0.1)',
        color: 'var(--bs-primary)',
      };
    }

    // Priority 4: Hover state
    if (isHovered) {
      return {
        ...baseStyle,
        backgroundColor: 'var(--bs-btn-hover-bg)',
        color: 'var(--bs-btn-hover-color)',
      };
    }

    // Default state
    return baseStyle;
  };

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isControlled) {
      validation.clearValidation();
    }

    onDateChange?.(date.toString());

    setTimeout(() => {
      onClose?.();
    }, CLOSE_DELAY_MS);
  };

  return (
    <div {...cellProps}>
      <button
        {...buttonProps}
        onClick={handleClick}
        style={getButtonStyle()}
        ref={ref}
        type="button"
        data-date={date.toString()}
        tabIndex={isFocused ? 0 : -1}
        className={clsx('btn fs-4 p-0', {
          [`btn-${color}`]: isSelected,
          'btn-flat-primary': !isSelected,
          disabled: isDisabled,
          'is-today position-relative': isToday,
        })}
        disabled={isDisabled}
        onMouseEnter={() => !isDisabled && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleClick(e);
          } else if (buttonProps.onKeyDown) {
            buttonProps.onKeyDown(e);
          }
        }}
      >
        {formattedDate}
      </button>
    </div>
  );
}

const CELL_SIZE = 40;
const CLOSE_DELAY_MS = 50;
